import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  Output,
  EventEmitter,
  Inject,
  input,
  signal,
  computed,
} from '@angular/core';
import { EmojiData } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { EMOJI_BG_IMAGE_URL, EMOJI_SET } from '../../u-emoji/u-emoji.consts';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SpriteCssEmojiPosition } from '../../../types/emoji-sprite-position';
import { IStyleService, STYLE_SERVICE } from '../../../injectionToken/style.service.interface';
import { Scheme } from '../../../types/scheme';
import { PopupRef } from '../../../services/popup/popup-ref';
import { UIconWrapperComponent } from '../u-icon-picker-wrapper/u-icon-picker-wrapper.component';
import { PopupService } from '../../../services/popup/popup.service';
import { distinctUntilChanged, take } from 'rxjs';
import { UiIconModel } from '../../../types/ui.icon.model';
import { INIT_POSITION_POPUP } from '../../../services/popup/popup-options';
import { UBaseInfraComponent } from '../../u-base-infra.component';
import { UIconModule } from '../../u-icon/u-icon.module';
import { UEmojiModule } from '../../u-emoji/u-emoji.module';
import { toObservable } from '@angular/core/rxjs-interop';
import { isEqual } from 'lodash';

declare type PickerButtonType = 'icon' | 'emoji';

export interface DisabledAction {
  id: string;
  disabledReason?: string;
}

@UntilDestroy()
@Component({
  selector: 'u-icon-picker',
  standalone: true,
  imports: [UIconModule, UEmojiModule],
  templateUrl: './u-icon-picker.component.html',
  styleUrls: ['./u-icon-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UIconPickerComponent extends UBaseInfraComponent {
  private _unicode: string;
  @Input()
  public set emojiUnicode(value: string) {
    this._unicode = value;
    this.pickerButtonType.set(value ? 'emoji' : 'icon');
    this.cdr.markForCheck();
  }
  public get emojiUnicode(): string {
    return this._unicode;
  }
  @Input()
  public set emojiSprite(value: SpriteCssEmojiPosition) {
    this.sheet = value;
    this.pickerButtonType.set(value ? 'emoji' : 'icon');
    this.cdr.markForCheck();
  }
  model = input<UiIconModel>({ type: 'font', value: 'icon-emoji-smile' });
  disabledActions = input<DisabledAction[]>([]);
  placeholderState = input<boolean>(null);
  @Input() placeholderColor = 'var(--color-text-primary)';
  @Input() placeholderFontSize = '18px';
  @Input() nativePrefix: string;
  @Input() title = 'Pick your emoji…';
  @Input() spriteEmojiSize = 24;
  @Input() closeOnSelect = false;
  @Input() autoFocus = true;
  @Input() color = 'var(--color-primary)';
  @Input() pickerEmojiSize = 24;
  @Input() darkMode: boolean;
  @Input() replacePlaceholderOnSelect = true;
  @Input() backgroundImageFn: (set: string, sheetSize: number) => string = () => EMOJI_BG_IMAGE_URL; // for now we support apple emojis only
  @Input() iconInSvg: boolean;

  @Output() selectedEmojiSprite = new EventEmitter<SpriteCssEmojiPosition>();
  @Output() selectedEmoji = new EventEmitter<EmojiData>();
  @Output() selectedFile = new EventEmitter<File>();
  @Output() all = new EventEmitter<SpriteCssEmojiPosition[]>();
  @Output() click = new EventEmitter<void>();
  @Output() popupStatus = new EventEmitter<boolean>();
  @Output() remove = new EventEmitter<void>();
  @Output() fileError = new EventEmitter<string>();

  theme: Scheme;
  sheet: SpriteCssEmojiPosition;

  private selectedFileModel = signal<UiIconModel>(null);
  pickerButtonType = signal<PickerButtonType>('icon');
  statusPopup = signal<boolean>(false);

  displayModel = computed(() => this.selectedFileModel() || this.model());

  pickerPosition: { top: string; left: string };
  private _selected: EmojiData;
  private _all: SpriteCssEmojiPosition[] = [];
  private readonly popupOffset = 12;

  constructor(
    private cdr: ChangeDetectorRef,
    private popupService: PopupService,
    @Inject(STYLE_SERVICE) private styleService: IStyleService
  ) {
    super();
    this.styleService.theme$.pipe(untilDestroyed(this)).subscribe((theme: Scheme) => {
      this.theme = theme;
      this.cdr.markForCheck();
    });

    toObservable(this.model)
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged((prev, next) => isEqual(prev, next))
      )
      .subscribe(() => {
        this.selectedFileModel.set(null);
      });
  }

  getIconRef() {
    return null;
  }

  private compPopupRef: PopupRef<UIconWrapperComponent, any>;
  openPickerPopup(event) {
    this.click.emit();
    this.statusPopup.set(true);
    this.popupStatus.emit(true);
    if (this.compPopupRef) {
      this.compPopupRef.destroy();
    }
    let { x, y } = event;
    y -= this.popupOffset;
    x += this.popupOffset;

    this.compPopupRef = this.popupService.open<UIconWrapperComponent, any>(
      { x, y },
      UIconWrapperComponent,
      {},
      { position: INIT_POSITION_POPUP }
    );
    this.compPopupRef.compInstance.disabledActions = this.disabledActions;
    this.compPopupRef.compInstance.set = EMOJI_SET;
    this.compPopupRef.compInstance.showPreview = false;
    this.compPopupRef.compInstance.emojiTooltip = true;
    this.compPopupRef.compInstance.autoFocus = this.autoFocus;
    this.compPopupRef.compInstance.color = this.color;
    this.compPopupRef.compInstance.emojiSize = this.pickerEmojiSize;
    this.compPopupRef.compInstance.darkMode = this.theme === 'dark';
    this.compPopupRef.compInstance.backgroundImageFn = this.backgroundImageFn;

    this.compPopupRef.compInstance.fileSelect.subscribe((val) => this.onFileSelected(val));
    this.compPopupRef.compInstance.emojiSelect.pipe(untilDestroyed(this)).subscribe((val) => this.emojiSelect(val));
    this.compPopupRef.compInstance.clear.pipe(untilDestroyed(this)).subscribe(() => this.reset());
    this.compPopupRef.compInstance.fileError.pipe(untilDestroyed(this)).subscribe((val) => this.fileError.emit(val));
    this.compPopupRef.destroy$.pipe(take(1)).subscribe(() => {
      this.statusPopup.set(false);
      this.popupStatus.emit(false);
    });
    this.cdr.markForCheck();
  }

  resetSelectedFile() {
    this.selectedFileModel.set(null);
    this.pickerButtonType.set('icon');
  }

  hasSelectedFile(): boolean {
    return this.selectedFileModel() !== null;
  }

  private reset() {
    this.remove.emit();
    this._selected = this.sheet = null;
    this.selectedFileModel.set(null);
    this.pickerButtonType.set('icon');
    if (this.closeOnSelect) {
      this.compPopupRef.destroy();
    }
  }

  private onFileSelected(file: File) {
    if (!file) {
      return;
    }
    if (this.replacePlaceholderOnSelect) {
      this.pickerButtonType.set('icon');
      this.selectedFileModel.set({ type: 'img', value: { lightUrl: URL.createObjectURL(file) } });
    }
    this.selectedFile.emit(file);
    if (this.closeOnSelect) {
      this.compPopupRef.destroy();
    }
  }

  emojiSelect(event) {
    if (!event) {
      return;
    }
    this.selectedFileModel.set(null);
    this._selected = event.emoji as EmojiData;
    this.sheet = this._selected.sheet;
    this.pickerButtonType.set(this.replacePlaceholderOnSelect ? 'emoji' : 'icon');

    this._all.push(this.sheet);
    this.all.emit(this._all);
    this.selectedEmojiSprite.emit(this.sheet);
    this.selectedEmoji.emit(this._selected);
    if (this.closeOnSelect) {
      this.compPopupRef.destroy();
    }
    this.cdr.markForCheck();
  }
}
